import { useLang } from '@/i18n';
import css from './index.module.scss';
import { useState, useEffect } from 'react';
import EcoImg from 'eco-design/base/eco-img';
import gtm from '@/utils/gtm';
import Modal from './Modal';
import { createPortal } from 'react-dom';
import { useWebpOrJpg } from '../../hooks/useWebpOrJpg';
import getLocal from '@/app/utils/i18n';

interface ItemType {
    tab: string;
    name: string;
    text: string;
    btn?: string;
    img: string[];
    webp?: string[];
}
const H: React.FC = () => {
    const { t } = useLang('dpux');
    const webpOrJpg = useWebpOrJpg('#delta-pro-ultra-x-H');
    const { locale } = getLocal();
    const [active, setActive] = useState(0);
    const [enter, setEnter] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [isClient, setIsClient] = useState(false);
    const items: ItemType[] = [
        {
            tab: t('H_item1_tab'),
            name: t.rich('H_item1_name', {
                s: (v: string) => <span className="s">{v}</span>,
            }),
            text: t('H_item1_text'),
            img: ['https://websiteoss.ecoflow.com/upload/image/1760167101391/pc1.jpg', 'https://websiteoss.ecoflow.com/upload/image/1760167101283/m1.jpg'],
        },
        {
            tab: t('H_item2_tab'),
            name: t.rich('H_item2_name', {
                s: (v: string) => <span className="s">{v}</span>,
            }),
            text: t('H_item2_text'),
            // btn: t('H_item2_btn'),
            img: [
                'https://websiteoss.ecoflow.com/upload/image/1760081465731/pc_Generator-Ready%20for%20Unlimited%20Backup.jpg',
                'https://websiteoss.ecoflow.com/upload/image/1760081466620/m_Generator-Ready%20for%20Unlimited%20Backup.jpg',
            ],
        },
        {
            tab: t('H_item3_tab'),
            name: t.rich('H_item3_name', {
                s: (v: string) => <span className="s">{v}</span>,
            }),
            text: t('H_item3_text'),
            img: [
                'https://websiteoss.ecoflow.com/upload/image/1760081467333/pc_10kW%20Solar%20Surge.jpg',
                'https://websiteoss.ecoflow.com/upload/image/1760081466250/m_10kW%20Solar%20Surge.jpg',
            ],
        },
    ];

    const handleChangeTab = (e: any, k: number) => {
        setActive(k);
        if (window.innerWidth < 1024) {
            e.target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
        }
        gtm.push({
            event: 'uaEvent',
            event_name: 'tab_click',
            list_name: t('H_title'),
            button_name: items[k].tab,
            __btn_id1: `dpux-H .tab-${k}`,
            __btn_id2: items[k].tab,
            __btn_type: 'text',
        });
    };

    const handleOpenModal = (btn: string) => {
        setModalOpen(true);
        gtm.push({
            __btn_id1: css.section,
            __btn_id2: btn,
        });
    };

    useEffect(() => {
        if (typeof window !== 'undefined') {
            setIsClient(true);
            const sceneHArr: any[] = [];
            const title: HTMLElement | null = document.querySelector('#delta-pro-ultra-x-H .title');
            if (title) {
                sceneHArr.push(
                    AniContainer.create({
                        triggerElement: '#delta-pro-ultra-x-H .info',
                        triggerHook: 1,
                        offset: title?.offsetHeight + 120,
                        duration: '100%',
                        onEnter: () => setEnter(true),
                        onProcess: (process: number) => {
                            if (process <= 0) {
                                setEnter(false);
                            }
                        },
                    }),
                );
            }
            return () => {
                sceneHArr.forEach((item) => {
                    item.destroy();
                });
            };
        }
    }, []);

    return (
        <section
            className={`${css.section} delta-pro-ultra-x-H dpux-H GtmModuleData-section`}
            id="delta-pro-ultra-x-H"
            data-section-id={'delta-pro-ultra-x-H'}
            data-section-title={t('H_title')}
        >
            <ul className="imgs">
                {items.map((item: ItemType, k: number) => (
                    <li className={`img${k === active ? ' active' : ''}`} key={item.tab}>
                        <EcoImg pc={webpOrJpg(item.img[0], item?.webp?.[0])} h5={webpOrJpg(item.img[1], item?.webp?.[1])} />
                    </li>
                ))}
            </ul>
            <div className="box">
                <div className="info">
                    <p className={`title${enter ? ' enter' : ''}`}>{t('H_title')}</p>
                </div>
                <div className="inner">
                    <ul className="tabs">
                        {items.map((item: ItemType, k: number) => (
                            <li
                                className={`tab${k === active ? ' active' : ''} tab-${k} GtmModuleData-btn-text`}
                                key={item.tab}
                                onClick={(e) => handleChangeTab(e, k)}
                            >
                                {item.tab}
                            </li>
                        ))}
                    </ul>
                    <ul className="contents">
                        {items.map((item: ItemType, k: number) => (
                            <li className={`content${k === active ? ' active' : ''}`} key={item.tab}>
                                <p className="content-name">{item.name}</p>
                                <p className="content-text">{item.text}</p>
                                {item?.btn && (
                                    <span className="content-btn GtmModuleData-btn-text" onClick={() => handleOpenModal(item?.btn || '')}>
                                        {item.btn}
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 25 24" fill="none">
                                            <path
                                                d="M12.5 24C19.1274 24 24.5 18.6274 24.5 12C24.5 5.37258 19.1274 0 12.5 0C5.87258 0 0.5 5.37258 0.5 12C0.5 18.6274 5.87258 24 12.5 24Z"
                                                fill="#3F68E0"
                                            />
                                            <path d="M8.125 12H16.875" stroke="white" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M12.5 7.625V16.375" stroke="white" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </span>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
            {isClient && modalOpen && createPortal(<Modal callback={() => setModalOpen(false)} />, document.body)}
        </section>
    );
};

export default H;
